"use client"

import { useState } from "react"
import { DialogShell } from "@/components/graphite/dialog-shell"
import { EmailDialog } from "@/components/email-dialog"
import { CreateContactWizard } from "./create-contact-wizard"
import {
  BulkAssignBar,
  CommunityToolbar,
  ContactsTable,
  type ContactRow,
} from "./graphite-community-tables"
import { asArray } from "@/lib/as-array"
import { PersonProfileSheet } from "./person-profile-sheet"
import {
  useGetBuckets,
  useGetContacts,
  usePostBucketsContacts,
} from "openapi/api/endpoints"
import { Contact } from "openapi/api/model"
import CommunityViewRoute from "@/pages/community/page"
import { useUrlSyncedSearchState } from "@/hooks/use-url-synced-search-state"
import type { CompanyeSearchParams } from "./companies-tab"

export function ContactSelector() {
  /* ---------------------------------------------
   * URL state (source of truth)
   * --------------------------------------------- */
  const navigate = CommunityViewRoute.useNavigate()
  const search = CommunityViewRoute.useSearch()
  const { patchSearch, clearSearchKeys } = useUrlSyncedSearchState<CompanyeSearchParams>(navigate)

  const isCreateDialogOpen = !!search.createContact

  /* ---------------------------------------------
   * Local UI state (non-URL)
   * --------------------------------------------- */
  const [selectedContacts, setSelectedContacts] = useState<string[]>([])
  const [selectedBucketForAssignment, setSelectedBucketForAssignment] = useState("")
  const [emailDialogOpen, setEmailDialogOpen] = useState(false)
  const [selectedContactForEmail, setSelectedContactForEmail] = useState<Contact | null>(null)
  const [selectedPersonId, setSelectedPersonId] = useState<string | null>(null)
  // 17c puts a search and a company filter over the table; there were neither.
  const [query, setQuery] = useState("")
  const [company, setCompany] = useState("all")

  /* ---------------------------------------------
   * Data
   * --------------------------------------------- */
  const { data: buckets, isLoading: bucketLoading } = useGetBuckets()
  const { mutateAsync: createdBucketContacts } = usePostBucketsContacts()
  const { data: contactList, isLoading, error } = useGetContacts()


  /* ---------------------------------------------
   * Derived data
   * --------------------------------------------- */
  /* ---------------------------------------------
   * Handlers
   * --------------------------------------------- */
  const handleContactSelect = (contactId: string) => {
    setSelectedContacts((prev) =>
      prev.includes(contactId) ? prev.filter((id) => id !== contactId) : [...prev, contactId],
    )
  }

  const handleBulkAssignToBucket = async () => {
    if (!selectedBucketForAssignment || selectedContacts.length === 0) return

    await createdBucketContacts({
      data: {
        bucket_id: selectedBucketForAssignment,
        contact_ids: selectedContacts,
      },
    })

    setSelectedContacts([])
    setSelectedBucketForAssignment("")
  }

  const openCreateContact = () => {
    patchSearch({ createContact: true })
  }

  const closeCreateContact = () => {
    clearSearchKeys(["createContact"])
  }

  const openPersonProfile = (contactId: string) => {
    setSelectedPersonId(contactId)
  }

  const openCompanyProfile = (companyId: string) => {
    navigate({
      to: "/community/companies",
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        companyId,
        companyTab: "contacts",
      }),
    })
  }

  /* ---------------------------------------------
   * Render
   * --------------------------------------------- */
  const all = asArray<Contact>(contactList)
  const needle = query.trim().toLowerCase()
  const rows: ContactRow[] = all
    .filter((contact) => company === "all" || contact.company_id === company)
    .filter((contact) => {
      if (!needle) return true
      const name = `${contact.first_name ?? ""} ${contact.last_name ?? ""}`.trim()
      return [name, contact.company_name, contact.title, contact.email].some((field) =>
        (field ?? "").toLowerCase().includes(needle),
      )
    })
    .map((contact) => {
      const name = `${contact.first_name ?? ""} ${contact.last_name ?? ""}`.trim()
      return {
        id: contact.id,
        name: name || "Unnamed contact",
        initials:
          `${contact.first_name?.[0] ?? ""}${contact.last_name?.[0] ?? ""}`.toUpperCase() || "?",
        company: contact.company_name,
        companyId: contact.company_id,
        title: contact.title ?? undefined,
        email: contact.email,
        type: contact.contact_type ?? undefined,
      }
    })

  /*
    A contact can carry a company id with no name — the old table showed those
    as "Unknown company" rather than making them unreachable, so the filter
    keeps an entry for them.
  */
  const companyOptions = [
    ...new Map(
      all
        .filter((contact) => contact.company_id)
        .map((contact) => [
          contact.company_id as string,
          contact.company_name ?? "Unknown company",
        ]),
    ),
  ].sort((a, b) => a[1].localeCompare(b[1]))

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <CommunityToolbar
        placeholder="Search contacts"
        value={query}
        onChange={setQuery}
        action="Create Contact"
        onAction={openCreateContact}
      >
        {companyOptions.length > 0 && (
          <span className="relative inline-flex items-baseline gap-2 border-b pb-1.5 pr-4 text-[12px]">
            {company === "all"
              ? "All companies"
              : (companyOptions.find(([id]) => id === company)?.[1] ?? "All companies")}
            <select
              value={company}
              onChange={(event) => setCompany(event.target.value)}
              aria-label="All companies"
              className="absolute inset-0 cursor-pointer opacity-0"
            >
              <option value="all">All companies</option>
              {companyOptions.map(([id, name]) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
            </select>
          </span>
        )}
      </CommunityToolbar>

      <BulkAssignBar
        count={selectedContacts.length}
        buckets={asArray<{ id?: string; name?: string }>(buckets)
          .filter((bucket) => bucket.id)
          .map((bucket) => ({ id: bucket.id as string, name: bucket.name ?? "Untitled" }))}
        bucketId={selectedBucketForAssignment}
        onBucketChange={setSelectedBucketForAssignment}
        onAssign={handleBulkAssignToBucket}
        onClear={() => setSelectedContacts([])}
        isBusy={false}
      />

      {error ? (
        <p className="py-6 text-[12.5px] text-muted-foreground">
          Contacts could not be loaded.
        </p>
      ) : (
        <ContactsTable
          rows={rows}
          selectedIds={selectedContacts}
          isLoading={isLoading || bucketLoading}
          onToggle={handleContactSelect}
          onOpenPerson={openPersonProfile}
          onOpenCompany={openCompanyProfile}
          onEmail={(id) => {
            const contact = all.find((entry) => entry.id === id)
            if (!contact) return
            setSelectedContactForEmail(contact)
            setEmailDialogOpen(true)
          }}
        />
      )}

      {/* Create Contact Dialog (URL-controlled) */}
      <DialogShell
        open={isCreateDialogOpen}
        onOpenChange={(open) => (open ? openCreateContact() : closeCreateContact())}
        wide
        title="Create New Contact"
      >
        {/* The wizard owns its own steps and actions, so no footer here. */}
        <CreateContactWizard onComplete={closeCreateContact} onCancel={closeCreateContact} />
      </DialogShell>

      {/* Email dialog */}
      {selectedContactForEmail && (
        <EmailDialog
          open={emailDialogOpen}
          onOpenChange={setEmailDialogOpen}
          recipientEmail={selectedContactForEmail.email ?? ""}
          recipientName={selectedContactForEmail.first_name}
          contactId={selectedContactForEmail.id}
        />
      )}

      <PersonProfileSheet
        personId={selectedPersonId}
        isOpen={!!selectedPersonId}
        onClose={() => setSelectedPersonId(null)}
      />
    </div>
  )
}
